import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, ChevronRight, Wallet, CheckCircle2, AlertCircle, FileText, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";

const EMPTY = {
  first_name: "", last_name: "", phone: "", email: "",
  date_of_birth: "", gender: "tjeter", address: "",
  medical_history: "", allergies: "", notes: "",
  recall_interval_months: 6, recall_enabled: true,
};

const STATUS_META = {
  no_charge:  { label: "Pa shërbim",   cls: "bg-[#EAE4D9] text-[#5C5C5C] border-[#C9C2B2]" },
  uninvoiced: { label: "Pa faturuar",  cls: "bg-[#D4A24C]/15 text-[#8B6914] border-[#D4A24C]/40" },
  unpaid:     { label: "Pa paguar",    cls: "bg-[#9E3C3C]/15 text-[#9E3C3C] border-[#9E3C3C]/40" },
  partial:    { label: "Pjesërisht",   cls: "bg-[#C9963A]/15 text-[#8B6914] border-[#C9963A]/40" },
  paid_full:  { label: "Paguar plotë", cls: "bg-[#2C4C3B] text-[#FDFBF7] border-[#2C4C3B]" },
  prepaid:    { label: "Parapaguar",   cls: "bg-[#4A8B71]/20 text-[#2C4C3B] border-[#4A8B71]/40" },
};

const FILTER_TABS = [
  { key: "all",        label: "Të Gjithë"   },
  { key: "uninvoiced", label: "Pa Faturuar" },
  { key: "unpaid",     label: "Pa Paguar"   },
  { key: "partial",    label: "Pjesërisht"  },
  { key: "paid_full",  label: "Paguar"      },
  { key: "prepaid",    label: "Parapaguar"  },
];

export default function Patients() {
  const { format } = useCurrency();
  const [list, setList] = useState([]);
  const [fin, setFin] = useState({});      // { patient_id: summary }
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [pRes, fRes] = await Promise.all([
      api.get(`/patients`, { params: q ? { q } : {} }),
      api.get(`/financial/patient-summaries`),
    ]);
    setList(pRes.data);
    setFin(fRes.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/patients", form);
      toast.success("Pacienti u shtua me sukses");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  // Apply filter + aggregate
  const filtered = useMemo(() => {
    if (filter === "all") return list;
    return list.filter((p) => {
      const f = fin[p.id];
      if (!f) return filter === "no_charge";
      return f.status === filter;
    });
  }, [list, fin, filter]);

  const totals = useMemo(() => {
    let invoiced = 0, paid = 0, debt = 0, uninvoiced = 0;
    for (const p of list) {
      const f = fin[p.id]; if (!f) continue;
      invoiced += f.invoiced_total;
      paid += f.paid_total;
      uninvoiced += f.uninvoiced_work;
      if (f.balance > 0) debt += f.balance;
    }
    return { invoiced, paid, debt, uninvoiced };
  }, [list, fin]);

  const counts = useMemo(() => {
    const c = { all: list.length };
    for (const p of list) {
      const f = fin[p.id];
      const s = f?.status || "no_charge";
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [list, fin]);

  return (
    <div className="space-y-8" data-testid="patients-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Klinika</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Pacientët</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Menaxho kartelat e pacientëve dhe gjendjen e pagesave.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-patient-button">
              <Plus size={16} /> Pacient i Ri
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader><DialogTitle>Shto Pacient</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <Field label="Emri">
                <Input value={form.first_name} required onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="patient-first-name" />
              </Field>
              <Field label="Mbiemri">
                <Input value={form.last_name} required onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="patient-last-name" />
              </Field>
              <Field label="Telefoni">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="patient-phone" />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="patient-email" />
              </Field>
              <Field label="Datëlindja">
                <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="patient-dob" />
              </Field>
              <Field label="Gjinia">
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="patient-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mashkull">Mashkull</SelectItem>
                    <SelectItem value="femer">Femër</SelectItem>
                    <SelectItem value="tjeter">Tjetër</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Adresa"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="patient-address" /></Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Historiku Mjekësor"><Textarea rows={2} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} data-testid="patient-history" /></Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Alergji"><Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} data-testid="patient-allergies" /></Field>
              </div>

              {/* Recall (rikontroll periodik) */}
              <div className="md:col-span-2 rounded-lg border border-[#E2DFD8] bg-[#FAF8F2] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="label-eyebrow">Rikontroll Periodik</Label>
                    <p className="text-xs text-[#5C5C5C] mt-1">Doktori cakton sa muaj pas kontrollit të fundit të kujtohet pacienti</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox"
                           checked={form.recall_enabled !== false}
                           onChange={(e) => setForm({ ...form, recall_enabled: e.target.checked })}
                           data-testid="patient-recall-enabled" />
                    Aktiv
                  </label>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {[3, 6, 9, 12, 24].map((m) => (
                    <button key={m} type="button"
                            onClick={() => setForm({ ...form, recall_interval_months: m })}
                            data-testid={`recall-preset-${m}`}
                            disabled={form.recall_enabled === false}
                            className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                              form.recall_interval_months === m
                                ? "border-[#2C4C3B] bg-[#2C4C3B] text-white"
                                : "border-[#E2DFD8] bg-white text-[#5C5C5C] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"
                            } ${form.recall_enabled === false ? "opacity-40 cursor-not-allowed" : ""}`}>
                      {m} muaj
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-[#5C5C5C]">Ose interval i personalizuar:</Label>
                  <Input type="number" min="1" max="60"
                         className="w-24"
                         value={form.recall_interval_months}
                         onChange={(e) => setForm({ ...form, recall_interval_months: Number(e.target.value) || 6 })}
                         disabled={form.recall_enabled === false}
                         data-testid="patient-recall-months" />
                  <span className="text-xs text-[#5C5C5C]">muaj</span>
                </div>
              </div>
              <DialogFooter className="md:col-span-2">
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="patient-save-button">
                  {saving ? "Duke ruajtur…" : "Ruaj"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Financial KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={FileText}        label="Faturuar gjithsej" value={format(totals.invoiced)} testId="kpi-invoiced" />
        <KPI icon={CheckCircle2}    label="Paguar gjithsej"   value={format(totals.paid)}     testId="kpi-paid" color="text-[#2C4C3B]" />
        <KPI icon={AlertCircle}     label="Borxhe aktive"     value={format(totals.debt)}     testId="kpi-debt"  color="text-[#9E3C3C]" />
        <KPI icon={Wallet}          label="Punë e pa faturuar" value={format(totals.uninvoiced)} testId="kpi-uninvoiced" color="text-[#8B6914]" />
      </div>

      {/* Search + filter chips */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <Input
            placeholder="Kërko sipas emri, mbiemri ose telefoni..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-11 bg-white"
            data-testid="patients-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="patients-filters">
          {FILTER_TABS.map((t) => (
            <button key={t.key} onClick={() => setFilter(t.key)}
                    data-testid={`filter-${t.key}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      filter === t.key
                        ? "bg-[#2C4C3B] text-[#FDFBF7]"
                        : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
                    }`}>
              {t.label}
              {(counts[t.key] || 0) > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filter === t.key ? "bg-white/20" : "bg-[#EAE4D9]"}`}>
                  {counts[t.key] || 0}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="surface-card overflow-hidden" data-testid="patients-list">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">
            {list.length === 0 ? "Nuk ka pacientë të regjistruar." : "Asnjë pacient nuk përputhet me filtrin."}
          </div>
        ) : (
          <ul className="divide-y divide-[#E2DFD8]">
            {filtered.map((p) => {
              const f = fin[p.id];
              const status = f?.status || "no_charge";
              const meta = STATUS_META[status] || STATUS_META.no_charge;
              return (
                <li key={p.id}>
                  <Link to={`/pacientet/${p.id}`}
                        className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-[#FDFBF7] transition-colors"
                        data-testid={`patient-${p.id}`}>
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#EAE4D9] text-[#2C4C3B] font-semibold flex-shrink-0">
                        {p.first_name?.[0]}{p.last_name?.[0]}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-[#1A1A1A] truncate">{p.first_name} {p.last_name}</div>
                        <div className="text-sm text-[#5C5C5C] truncate">
                          {p.phone || p.email || "Pa kontakt"}
                          {p.date_of_birth ? ` · ${p.date_of_birth}` : ""}
                        </div>
                      </div>
                    </div>

                    {/* Financial info */}
                    <div className="hidden md:flex items-center gap-6 text-xs text-[#5C5C5C] flex-shrink-0">
                      {f && f.invoiced_total > 0 && (
                        <div className="text-right">
                          <div className="label-eyebrow">Paguar</div>
                          <div className="font-mono text-sm text-[#1A1A1A]">{format(f.paid_total)} / {format(f.invoiced_total)}</div>
                        </div>
                      )}
                      {f && f.balance > 0 && (
                        <div className="text-right">
                          <div className="label-eyebrow text-[#9E3C3C]">Borxh</div>
                          <div className="font-mono text-sm text-[#9E3C3C] font-semibold">{format(f.balance)}</div>
                        </div>
                      )}
                      {f && f.balance < 0 && (
                        <div className="text-right">
                          <div className="label-eyebrow text-[#2C4C3B]">Kredi</div>
                          <div className="font-mono text-sm text-[#2C4C3B] font-semibold">{format(-f.balance)}</div>
                        </div>
                      )}
                      {f && f.uninvoiced_work > 0 && (
                        <div className="text-right">
                          <div className="label-eyebrow text-[#8B6914]">Pa faturuar</div>
                          <div className="font-mono text-sm text-[#8B6914]">{format(f.uninvoiced_work)}</div>
                        </div>
                      )}
                    </div>

                    <span className={`rounded-full px-3 py-1 text-[11px] font-medium border ${meta.cls}`} data-testid={`status-${p.id}`}>
                      {meta.label}
                    </span>
                    <ChevronRight size={18} className="text-[#5C5C5C] flex-shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color = "text-[#1A1A1A]", testId }) {
  return (
    <div className="surface-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#5C5C5C]">
        <Icon size={14} />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className={`mt-2 text-xl font-semibold tracking-tight font-mono ${color}`}>{value}</div>
    </div>
  );
}
